import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { LATEST_TXN_DATES_QUERY, OCCUPANCY_TRANSACTIONS_QUERY, STORES_DETAIL_QUERY } from '../graphql/queries';
import { useSelectedStore } from '../hooks/useSelectedStore';
import { eventFor, useCalendarEvents } from '../hooks/useCalendarEvents';
import { gql } from '../lib/api';
import { airRegiAccountHour, airRegiBusinessDate, airRegiService } from '../lib/airRegiBusinessTime';
import { type StoreLD } from '../lib/salesAnalytics';
import { calculateOccupancyDay, type OccupancyDay, type OccupancyPoint, type StayTransaction } from '../lib/stayOccupancy';

interface Store extends StoreLD {
  name: string;
  seatCount: number | null;
  airregiBusinessDayStart: string | null;
}

interface Transaction extends StayTransaction {
  storeId: string;
}

type Segment = 'all' | 'lunch' | 'dinner';

interface DisplayOption {
  key: string;
  store: Store;
  segment: Segment;
  label: string;
}

interface DailySeries extends OccupancyDay {
  date: string;
  weekday: string;
  supplemented: boolean;
  eventTitle?: string;
}

const DAY_LABELS = ['火', '水', '木', '金', '土', '日'] as const;
const toDate = (date: string): Date => new Date(`${date}T00:00:00Z`);
const addDays = (date: string, amount: number): string => {
  const next = toDate(date);
  next.setUTCDate(next.getUTCDate() + amount);
  return next.toISOString().slice(0, 10);
};
const businessWeekRange = (date: string): { from: string; to: string } => {
  const daysFromTuesday = (toDate(date).getUTCDay() + 5) % 7;
  const from = addDays(date, -daysFromTuesday);
  return { from, to: addDays(from, 5) };
};
const todayInJapan = (): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};
const longDate = (date: string): string => {
  const [year, month, day] = date.split('-');
  return `${year}年${month}月${day}日`;
};
const shortDate = (date: string): string => date.slice(5).replace('-', '/');
const validDate = (value: string | null): value is string => /^\d{4}-\d{2}-\d{2}$/.test(value ?? '');
const fmtPeople = (value: number | null): string => value == null ? 'データなし' : `${value.toFixed(1)}人`;
const fmtMinutes = (value: number | null): string => value == null ? 'データなし' : `${Math.round(value)}分`;
const fmtPercent = (value: number | null): string => value == null ? 'データなし' : `${value.toFixed(1)}%`;

const displayOptionsFor = (stores: Store[]): DisplayOption[] =>
  [...stores]
    .sort((a, b) => Number(!!b.lunchStart) - Number(!!a.lunchStart))
    .flatMap((store): DisplayOption[] => store.lunchStart && store.lunchEnd
      ? [
        { key: `${store.id}:lunch`, store, segment: 'lunch', label: `${store.name}（ランチ）` },
        { key: `${store.id}:dinner`, store, segment: 'dinner', label: `${store.name}（ディナー）` },
      ]
      : [{ key: `${store.id}:all`, store, segment: 'all', label: store.name }]);

export const StayOccupancyPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedStoreId, setStoredStoreId] = useSelectedStore();
  const initialStoreId = searchParams.get('store') || storedStoreId;
  const initialSegment = searchParams.get('segment');
  const initialDate = searchParams.get('date');
  const [selectedKey, setSelectedKey] = useState(
    `${initialStoreId}:${initialSegment === 'lunch' || initialSegment === 'dinner' ? initialSegment : 'all'}`,
  );
  const [selectedDate, setSelectedDate] = useState(validDate(initialDate) ? initialDate : '');

  const storesQ = useQuery({
    queryKey: ['storesDetail'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_DETAIL_QUERY),
  });
  const stores = useMemo(() => storesQ.data?.stores ?? [], [storesQ.data?.stores]);
  const displayOptions = useMemo(() => displayOptionsFor(stores), [stores]);
  const requestedStoreId = selectedKey.slice(0, selectedKey.lastIndexOf(':'));
  const selectedOption = displayOptions.find((option) => option.key === selectedKey)
    ?? displayOptions.find((option) => option.store.id === requestedStoreId)
    ?? displayOptions[0];
  const storeId = selectedOption?.store.id ?? '';
  const segment = selectedOption?.segment ?? 'all';
  const seatCount = selectedOption?.store.seatCount ?? null;

  useEffect(() => {
    if (!selectedOption) return;
    if (selectedKey !== selectedOption.key) setSelectedKey(selectedOption.key);
    setStoredStoreId(storeId);
  }, [selectedKey, selectedOption, setStoredStoreId, storeId]);

  useEffect(() => {
    if (!storeId) return;
    const next = new URLSearchParams(searchParams);
    next.set('store', storeId);
    if (segment === 'all') next.delete('segment');
    else next.set('segment', segment);
    if (selectedDate) next.set('date', selectedDate);
    else next.delete('date');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [searchParams, segment, selectedDate, setSearchParams, storeId]);

  const today = useMemo(todayInJapan, []);
  const latestQ = useQuery({
    enabled: !!storeId,
    queryKey: ['stayOccupancyLatest', storeId, segment, stores, today],
    queryFn: async () => {
      for (let offset = 0; offset <= 60; offset += 7) {
        const days = await Promise.all(Array.from({ length: Math.min(7, 61 - offset) }, (_, index) => {
          const date = addDays(today, -offset - index);
          return gql<{ transactions: Pick<Transaction, 'storeId' | 'visitDate' | 'visitTime' | 'accountDate' | 'accountTime'>[] }>(
            LATEST_TXN_DATES_QUERY, { storeId, from: date, to: date },
          );
        }));
        const store = stores.find((item) => item.id === storeId);
        const matching = days.flatMap((day) => day.transactions).filter((transaction) =>
          segment === 'all' || airRegiService(transaction, store) === segment);
        if (matching.length) return matching.map((transaction) =>
          airRegiBusinessDate(transaction, store?.airregiBusinessDayStart)).sort().at(-1) ?? '';
      }
      return '';
    },
  });
  const latestDate = latestQ.data ?? '';
  const latestWeekDate = latestDate ? businessWeekRange(latestDate).to : '';

  useEffect(() => {
    if (!selectedDate && latestWeekDate) setSelectedDate(latestWeekDate);
  }, [latestWeekDate, selectedDate]);

  const weekRange = useMemo(() => selectedDate ? businessWeekRange(selectedDate) : null, [selectedDate]);
  const fetchRange = useMemo(() => weekRange
    ? { from: addDays(weekRange.from, -7), to: addDays(weekRange.to, 1) }
    : null, [weekRange]);
  const eventsQ = useCalendarEvents(fetchRange?.from, fetchRange?.to);
  const transactionsQ = useQuery({
    enabled: !!storeId && !!fetchRange,
    queryKey: ['stayOccupancyTransactions', storeId, fetchRange?.from, fetchRange?.to],
    queryFn: async () => {
      const transactions: Transaction[] = [];
      for (let offset = 0; offset < 14; offset += 7) {
        const days = await Promise.all(Array.from({ length: 7 }, (_, index) => {
          const date = addDays(fetchRange!.from, offset + index);
          return gql<{ transactions: Transaction[] }>(OCCUPANCY_TRANSACTIONS_QUERY, { storeId, from: date, to: date });
        }));
        transactions.push(...days.flatMap((day) => day.transactions));
      }
      return { transactions };
    },
  });

  const series = useMemo<DailySeries[]>(() => {
    if (!weekRange || !selectedOption) return [];
    const store = selectedOption.store;
    const matching = (transactionsQ.data?.transactions ?? []).filter((transaction) =>
      segment === 'all' || airRegiService(transaction, store) === segment);
    const startTime = segment === 'lunch' ? store.lunchStart : store.dinnerStart;
    const endTime = segment === 'lunch' ? store.lunchEnd : store.dinnerEnd;
    const startHour = startTime ? Number(startTime.split(':')[0]) : 0;
    const configuredEndHour = endTime
      ? Math.ceil(Number(endTime.split(':')[0]) + Number(endTime.split(':')[1]) / 60)
      : 24;
    const lastAccountHour = Math.max(configuredEndHour - 1, ...matching
      .map((transaction) => airRegiAccountHour(transaction, store.airregiBusinessDayStart))
      .filter((hour): hour is number => hour !== null));
    const endHour = lastAccountHour + 3;
    return DAY_LABELS.map((weekday, index) => {
      const selected = addDays(weekRange.from, index);
      const event = eventFor(eventsQ.data?.calendarEvents, storeId, selected);
      const hasCurrent = matching.some((transaction) =>
        airRegiBusinessDate(transaction, store.airregiBusinessDayStart) === selected);
      const prior = addDays(selected, -7);
      const hasPrior = matching.some((transaction) =>
        airRegiBusinessDate(transaction, store.airregiBusinessDayStart) === prior);
      const supplemented = !event && !eventFor(eventsQ.data?.calendarEvents, storeId, prior) && !hasCurrent && hasPrior;
      const date = supplemented ? prior : selected;
      return { date, weekday, supplemented, eventTitle: event?.title,
        ...calculateOccupancyDay(event ? [] : matching, date, startHour, endHour, seatCount, store.airregiBusinessDayStart ?? '05:00') };
    });
  }, [eventsQ.data, seatCount, segment, selectedOption, storeId, transactionsQ.data, weekRange]);

  const validStayCount = series.reduce((sum, day) => sum + (day.reliableStay ? day.validStayCount : 0), 0);
  const weeklyStay = validStayCount
    ? series.reduce((sum, day) => sum + (day.averageStayMinutes ?? 0) * (day.reliableStay ? day.validStayCount : 0), 0) / validStayCount
    : null;
  const peakOccupied = series.some((day) => day.peakOccupied != null)
    ? Math.max(...series.map((day) => day.peakOccupied ?? 0)) : null;
  const peakRate = peakOccupied != null && seatCount ? peakOccupied / seatCount * 100 : null;
  const totalTransactions = series.reduce((sum, day) => sum + day.transactionCount, 0);
  const validOccupancyCount = series.reduce((sum, day) => sum + day.validOccupancyCount, 0);
  const totalCustomers = series.reduce((sum, day) => sum + day.customers, 0);
  const maxStay = Math.max(0, ...series.flatMap((day) => day.points.map((point) => point.stayMinutes ?? 0)));
  const occupancyAxisMax = Math.max(seatCount ?? 0, peakOccupied ?? 0, 1);
  const stayAxisMax = Math.max(30, Math.ceil(maxStay / 30) * 30);
  const isLoading = storesQ.isLoading || latestQ.isLoading || transactionsQ.isLoading || eventsQ.isLoading;
  const hasError = storesQ.isError || latestQ.isError || transactionsQ.isError || eventsQ.isError;

  return (
    <div className="kpi-weekly-page kpi-stay-page">
      <header className="kpi-weekly-heading">
        <div>
          <div className="kpi-weekly-kicker">KPI LAB / SEAT OCCUPANCY</div>
          <h1>滞在時間と人数</h1>
          <p>火曜日から日曜日まで、時間帯別の推定在席人数と平均滞在時間を1日1グラフで比較します。</p>
        </div>
        <label className="kpi-weekly-store">
          <span>表示店舗・時間帯</span>
          <select value={selectedOption?.key ?? ''} onChange={(event) => setSelectedKey(event.target.value)}>
            {displayOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
          </select>
        </label>
      </header>

      <div className="kpi-weekly-note">
        <span>KPI検討用</span>
        <p>来店したAirレジ営業日と店舗マスタの集計終点で分類し、伝票作成から会計までの1時間平均在席人数と平均滞在時間を推定します。横軸は最終会計から2時間先まで表示し、イベント日は除外します。</p>
      </div>

      <div className="kpi-weekly-range-controls">
        <label>
          <span>表示する週の基準日</span>
          <input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} />
        </label>
        <div className="kpi-weekly-range-actions">
          <button type="button" disabled={!selectedDate} onClick={() => setSelectedDate(addDays(selectedDate, -7))}>← 前の週</button>
          <button type="button" disabled={!selectedDate} onClick={() => setSelectedDate(addDays(selectedDate, 7))}>次の週 →</button>
          <button type="button" disabled={!latestWeekDate} onClick={() => setSelectedDate(latestWeekDate)}>最新週</button>
        </div>
      </div>

      {hasError && <section className="kpi-weekly-state" role="alert">データを取得できませんでした。時間をおいて再度お試しください。</section>}
      {!hasError && isLoading && <section className="kpi-weekly-state">滞在・在席データを読み込んでいます…</section>}
      {!hasError && !isLoading && !latestDate && !selectedDate && (
        <section className="kpi-weekly-state">この店舗の直近60日間に会計データがありません。日付を選択すると過去のデータを表示できます。</section>
      )}
      {!hasError && !isLoading && weekRange && (
        <>
          <section className="kpi-weekly-overview">
            <div className="kpi-weekly-period">
              <span>対象営業週</span>
              <strong>{longDate(weekRange.from)} — {longDate(weekRange.to)}</strong>
              <small>{selectedOption?.label} ／ 座席数 {seatCount != null ? `${seatCount}席` : '未登録'} ／ 来客 {totalCustomers.toLocaleString()}人</small>
            </div>
            <div className="kpi-weekly-total kpi-weekly-total-sales">
              <span>ピーク時の平均在席人数</span>
              <strong>{fmtPeople(peakOccupied)}</strong>
              <small>座席稼働率 {fmtPercent(peakRate)} ／ 1時間平均の最大</small>
            </div>
            <div className="kpi-weekly-total kpi-weekly-total-customers">
              <span>平均滞在時間</span>
              <strong>{fmtMinutes(weeklyStay)}</strong>
              <small>データが十分な日の有効 {validStayCount.toLocaleString()} 組の平均</small>
            </div>
          </section>

          {seatCount == null && <p className="kpi-stay-alert">座席数が店舗マスタに登録されていないため、座席稼働率と座席数の基準線は表示できません。</p>}
          {totalTransactions > 0 && validOccupancyCount < totalTransactions && (
            <p className="kpi-stay-alert">在席人数を推定できた伝票は {validOccupancyCount.toLocaleString()} / {totalTransactions.toLocaleString()} 件です。対象が全伝票の80%未満の日は在席人数と座席稼働率を表示せず、週間集計から除外します。</p>
          )}

          <section className="kpi-weekly-chart-panel">
            <div className="kpi-weekly-chart-heading">
              <div>
                <span>TUESDAY — SUNDAY</span>
                <h2>曜日別・時間別の在席人数と滞在時間</h2>
              </div>
              <p>月曜日は定休日のため表示していません</p>
            </div>
            <div className="kpi-weekly-legend" aria-label="グラフの凡例">
              <span><i className="kpi-weekly-legend-sales" />平均在席人数</span>
              <span><i className="kpi-weekly-legend-customers" />平均滞在時間</span>
              {seatCount != null && <span><i className="kpi-weekly-legend-target" />座席数 {seatCount}席</span>}
            </div>

            <div className="kpi-weekly-chart-grid">
              {series.map((day) => (
                <article className="kpi-weekly-chart-card" key={day.weekday}>
                  <header>
                    <div>
                      <span>{day.weekday}曜日</span>
                      <h3>{longDate(day.date)}（{day.weekday}）</h3>
                    </div>
                    {day.supplemented && <b>前週から補完</b>}
                    {day.eventTitle && <b className="kpi-event-badge">{day.eventTitle}・KPI除外</b>}
                  </header>
                  {day.eventTitle ? <div className="kpi-event-excluded">イベント日をKPI集計から除外しました<small>元データは保持されています</small></div> : <>
                  <div className="kpi-weekly-card-totals">
                    <span className="kpi-weekly-card-sales">ピーク平均在席 {fmtPeople(day.peakOccupied)}</span>
                    <span className="kpi-weekly-card-customers">平均滞在 {fmtMinutes(day.averageStayMinutes)}</span>
                    <span>来客 {day.customers.toLocaleString()}人</span>
                    <span className="kpi-stay-card-coverage">推定対象 {day.validOccupancyCount} / {day.transactionCount} 組{day.transactionCount > 0 && (!day.reliableOccupancy || !day.reliableStay) ? ' ／ データ不足' : ''}</span>
                  </div>
                  <div className="kpi-weekly-chart" aria-label={`${shortDate(day.date)}の時間別平均在席人数と平均滞在時間の折れ線グラフ`}>
                    {!day.reliableOccupancy && !day.reliableStay ? (
                      <div className="kpi-stay-no-data">{day.transactionCount ? '計算可能な伝票が80%未満のためデータ不足' : '会計データなし'}</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={day.points} margin={{ top: 12, right: 2, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="2 5" vertical={false} />
                          <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#65605d', fontSize: 11 }} />
                          <YAxis yAxisId="people" domain={[0, occupancyAxisMax]} axisLine={false} tickLine={false}
                            width={45} tickCount={4} tick={{ fill: '#65605d', fontSize: 9 }}
                            tickFormatter={(value) => `${Number(value).toFixed(0)}人`} />
                          <YAxis yAxisId="stay" domain={[0, stayAxisMax]} orientation="right" axisLine={false}
                            tickLine={false} width={36} tickCount={4} tick={{ fill: '#65605d', fontSize: 9 }}
                            tickFormatter={(value) => `${Math.round(Number(value))}分`} />
                          {seatCount != null && <ReferenceLine yAxisId="people" y={seatCount} stroke="#b56a00"
                            strokeWidth={2} strokeDasharray="7 5" ifOverflow="extendDomain" />}
                          <Tooltip
                            labelFormatter={(label) => `${longDate(day.date)} ${label}`}
                            formatter={(value, name, item) => {
                              const point = item.payload as OccupancyPoint;
                              return name === '平均在席人数'
                                ? [`${Number(value).toFixed(1)}人（座席稼働率 ${fmtPercent(point.occupancyRate)}）`, name]
                                : [`${Math.round(Number(value))}分（${point.completedGroups}組）`, name];
                            }}
                          />
                          <Line yAxisId="people" type="monotone" dataKey="occupied" name="平均在席人数"
                            stroke="#0088b0" strokeWidth={2.5} connectNulls={false}
                            dot={{ r: 3, fill: '#faf9f8', strokeWidth: 2 }} activeDot={{ r: 5 }} />
                          <Line yAxisId="stay" type="monotone" dataKey="stayMinutes" name="平均滞在時間"
                            stroke="#d6006c" strokeWidth={2.5} connectNulls={false}
                            dot={{ r: 3, fill: '#faf9f8', strokeWidth: 2 }} activeDot={{ r: 5 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  </>}
                </article>
              ))}
            </div>

            <h3 className="kpi-weekly-days-title">表示6日間の日別実績</h3>
            <div className="kpi-weekly-days" aria-label="表示6日間の日別実績">
              {series.map((day) => (
                <article key={day.weekday}>
                  <div><strong>{day.weekday}</strong><span>{shortDate(day.date)}</span>{day.supplemented && <span>前週補完</span>}</div>
                  {day.eventTitle ? <p className="kpi-event-badge">{day.eventTitle}・KPI除外</p> : <dl>
                    <div><dt>来客</dt><dd>{day.customers.toLocaleString()}人</dd></div>
                    <div><dt>ピーク平均在席</dt><dd>{fmtPeople(day.peakOccupied)}</dd></div>
                    <div><dt>座席稼働率</dt><dd>{fmtPercent(day.peakOccupancyRate)}</dd></div>
                    <div><dt>平均滞在</dt><dd>{fmtMinutes(day.averageStayMinutes)}</dd></div>
                  </dl>}
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};
